// Throwing a grenade (qa-runs#1171 part 3; the server half is vtt/server/src/blasts.ts): the form behind "Throw
// grenade…" in the dice bag, only where the system's content has a blast rule page. The thrower (a token of this
// person's; the DM's any), the grenade's kind and the way it is thrown (the page's names), the skill rating and pool
// dice, and the spot aimed at — a tap on the board — with a few words for it ("the door"). The DM may mark walls broken
// for this blast (a tap on each). Before the throw the form previews who a blast landing true would catch, from what
// this person can see; the server throws, scatters, lays the burst and makes each creature caught a staged attack
// waiting for its resistance — its player (or the DM) is asked for pool dice in the log where the system's resistance
// draws on a pool, as a ranged attack's defender is. After the throw the scatter is drawn from the aim to the blast,
// and the blast waits to go off (the issue's default: the DM can move it after the scatter, before anyone resists):
// the thrower sets it off where it landed, or the DM taps a spot to move it to first and sets it off there. The DM can
// set off any thrower's held blast from this form (pick the thrower). The server checks the pool dice too.
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { blastCaught, blastReach, tokenDistance, type Area, type Wall } from '@dragons-table/vtt-shared';
import { selection } from '../../board/selection';
import { blastAim } from '../../board/blastAim';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { useTableExtras } from '../../tableExtras';
import { BTN } from '../ui';

const btn = `${BTN} min-h-[2rem] [@media(pointer:coarse)]:min-h-[2.75rem] [@media(pointer:coarse)]:px-3`;
const field = 'rounded bg-stone-800 px-1 py-1 text-xs';
const int = (v: string): number | null => (v.trim() !== '' && Number.isInteger(Number(v)) ? Number(v) : null);

interface TokenLike { id: string; kind: string; name?: string; mine?: boolean; owners?: string[]; x: number; y: number; dead?: boolean }
interface Fighting { tokens: string[]; pools?: Record<string, { left: number; max?: number }> }

/** Whether the dice bag shows "Throw grenade…": the system's content has a blast rule. */
export function useBlastsOffered(): boolean {
  const { campaignId } = useShell();
  return useTableExtras(campaignId).blast !== null;
}

export function BlastForm({ isDm, priv, onClose }: { isDm: boolean; priv: boolean; onClose: () => void }) {
  const { store, act, setNotice, systemRules } = useTableState();
  const { campaignId } = useShell();
  const { blast } = useTableExtras(campaignId);
  const aim = useSyncExternalStore(blastAim.subscribe, blastAim.get);
  useEffect(() => { blastAim.setOpen(true); return () => blastAim.setOpen(false); }, []);
  const objects = store.state.objects as Record<string, TokenLike>;
  const tokens = Object.values(objects).filter((o) => o.kind === 'token');
  const mine = tokens.filter((o) => isDm || o.mine);
  const [thrower, setThrower] = useState<string>(() => [...selection.get()].find((id) => mine.some((o) => o.id === id)) ?? mine[0]?.id ?? '');
  const [kind, setKind] = useState(blast?.kinds[0]?.key ?? '');
  const [type, setType] = useState(blast?.types[0]?.key ?? '');
  const [rating, setRating] = useState('');
  const [pool, setPool] = useState('0');
  const [aimedAt, setAimedAt] = useState('');
  const [sending, setSending] = useState(false);
  useEffect(() => { if (blast && !kind) setKind(blast.kinds[0]?.key ?? ''); if (blast && !type) setType(blast.types[0]?.key ?? ''); }, [blast, kind, type]);
  const k = blast?.kinds.find((x) => x.key === kind);
  const t = objects[thrower];
  const grid = store.state.grid as { x: { basePitch: number }; y: { basePitch: number } };
  const distance = systemRules.distance;
  const pxPerMetre = distance ? grid.x.basePitch / distance.perSquare : null;
  const reachPx = k && pxPerMetre ? blastReach(k.power, k.perMetre) * pxPerMetre : null;
  useEffect(() => { blastAim.setPreview(t ? { x: t.x, y: t.y } : null, reachPx); }, [t?.x, t?.y, reachPx, t]);
  // The thrower's pool left (the system's first pool that adds dice), as this person was sent it.
  const enc = store.state.encounter as { combatants?: Fighting[] } | undefined;
  const poolRule = (systemRules.pools ?? [])[0];
  const left = poolRule ? enc?.combatants?.find((c) => c.tokens.includes(thrower))?.pools?.[poolRule.key]?.left ?? null : null;
  const metres = t && aim.aimed && distance ? tokenDistance(t as never, aim.aimed, grid as never, distance.perSquare) : null;
  // Who a blast landing true would catch: a preview from what this store holds (the DM's whole, a player's seen).
  const preview = useMemo(() => {
    if (!aim.aimed || !k || !pxPerMetre) return [];
    const walls = (store.state.walls ?? []) as Wall[];
    return blastCaught({ point: aim.aimed, power: k.power, perMetre: k.perMetre, targets: tokens.map((o) => ({ id: o.id, x: o.x, y: o.y, w: 0, h: 0, ...(o.dead ? { dead: true } : {}) })), walls, broken: aim.broken, pxPerMetre });
  }, [aim.aimed, aim.broken, k, pxPerMetre, tokens, store.state.walls]);
  if (!blast) return <p className="text-xs text-stone-400">This table's game system has no rule for thrown blasts.</p>;
  const r = int(rating); const p = int(pool) ?? 0;
  const ready = Boolean(t && k && type && aim.aimed && r !== null && r >= 0 && p >= 0 && (left === null || p <= left));
  const send = () => {
    if (!ready || !aim.aimed) return;
    setSending(true);
    const before = new Set(Object.keys((store.state.areas ?? {}) as Record<string, Area>));
    act({ kind: 'blast.throw', token: thrower, aimed: { x: Math.round(aim.aimed.x), y: Math.round(aim.aimed.y) }, aimedAt: aimedAt.trim() || 'the spot', type, blast: kind, rating: r!, pool: p, modifiers: [], broken: [...aim.broken], private: isDm && priv } as never,
      (reply) => { setSending(false); if (!reply.ok) setNotice(reply.reason ?? 'That throw could not be made.'); else blastAim.thrown(before); });
  };
  const setOff = () => {
    if (!thrower) return;
    setSending(true);
    act({ kind: 'blast.throw', token: thrower, land: true, ...(isDm && aim.moveTo ? { moveTo: { x: Math.round(aim.moveTo.x), y: Math.round(aim.moveTo.y) } } : {}) } as never,
      (reply) => { setSending(false); if (!reply.ok) setNotice(reply.reason ?? 'That blast could not be set off.'); else blastAim.wentOff(); });
  };
  const unit = distance?.unit ?? '';
  return (
    <div className="mt-1 flex flex-col gap-1.5 rounded border border-stone-700 p-2 text-xs" data-testid="blast-form">
      <div className="flex flex-wrap items-center gap-1.5">
        <label className="flex items-center gap-1">Thrower
          <select className={field} value={thrower} onChange={(e) => setThrower(e.target.value)} aria-label="Thrower">
            {mine.map((o) => <option key={o.id} value={o.id}>{o.name || 'Token'}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">Grenade
          <select className={field} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Grenade">
            {blast.kinds.map((x) => <option key={x.key} value={x.key}>{x.label} (Power {x.power}, −{x.perMetre}/{unit || 'unit'})</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">Throw
          <select className={field} value={type} onChange={(e) => setType(e.target.value)} aria-label="Throw type">
            {blast.types.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
          </select>
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <label className="flex items-center gap-1">Skill
          <input aria-label="Skill rating" type="number" min={0} max={60} inputMode="numeric" className={`${field} w-14`} value={rating} onChange={(e) => setRating(e.target.value)} />
        </label>
        {poolRule && (
          <label className="flex items-center gap-1">{poolRule.label}
            <input aria-label={`${poolRule.label} dice`} type="number" min={0} max={left ?? 60} inputMode="numeric" className={`${field} w-14`} value={pool} onChange={(e) => setPool(e.target.value)} />
            {left !== null && <span className="text-stone-400">of {left} left</span>}
          </label>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" className={`${btn} ${aim.aiming ? 'border-amber-500 text-amber-200' : ''}`} data-testid="blast-aim" onClick={() => (aim.aiming ? blastAim.stopAim() : blastAim.startAim())}>
          {aim.aiming ? 'Tap the board…' : aim.aimed ? 'Aim again' : 'Aim on the board'}
        </button>
        <input aria-label="What it is aimed at" placeholder="aimed at (the door)" maxLength={30} className={`${field} w-36`} value={aimedAt} onChange={(e) => setAimedAt(e.target.value)} />
        {metres !== null && <span className="text-stone-400" data-testid="blast-distance">{Math.round(metres * 10) / 10} {unit} away</span>}
      </div>
      {isDm && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button type="button" className={`${btn} ${aim.marking ? 'border-red-500 text-red-200' : ''}`} data-testid="blast-mark-walls" onClick={() => blastAim.setMarking(!aim.marking)}>
            {aim.marking ? 'Done marking walls' : 'Mark broken walls'}
          </button>
          <span className="text-stone-400">{aim.broken.size ? `${aim.broken.size} wall${aim.broken.size === 1 ? '' : 's'} broken for this blast` : 'Every wall holds.'}</span>
          {aim.broken.size > 0 && <button type="button" className={btn} onClick={() => blastAim.clearWalls()}>Clear</button>}
        </div>
      )}
      {aim.aimed && k && (
        <div data-testid="blast-preview">
          <p className="text-stone-400">If it lands true, it catches:</p>
          {preview.length === 0 ? <p className="text-stone-500">nobody you can see.</p> : (
            <ul className="ml-3 list-disc">
              {preview.map((c) => <li key={c.id}>{objects[c.id]?.name || 'A creature'}: {c.metres} {unit}, Power {c.power}</li>)}
            </ul>
          )}
          <p className="text-stone-500">Where it really lands is rolled. Each creature caught resists in the log; a player is asked for pool dice where the rules allow.</p>
        </div>
      )}
      {(aim.held || isDm) && (
        <div className="flex flex-col gap-1 rounded border border-red-900 p-1.5" data-testid="blast-held">
          <p className="text-stone-300">{aim.held ? 'The blast has landed. Nobody is caught until it goes off.' : "A blast this thrower threw that hasn't gone off yet:"}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {isDm && (
              <button type="button" className={`${btn} ${aim.moving ? 'border-red-500 text-red-200' : ''}`} data-testid="blast-move" onClick={() => (aim.moving ? blastAim.stopMove() : blastAim.startMove())}>
                {aim.moving ? 'Tap the board…' : aim.moveTo ? 'Move again' : 'Move the blast'}
              </button>
            )}
            {isDm && aim.moveTo && <button type="button" className={btn} onClick={() => blastAim.clearMove()}>Where it landed</button>}
            <button type="button" className={`${btn} border-red-600`} disabled={sending || !thrower} data-testid="blast-set-off" onClick={setOff}>{isDm && aim.moveTo ? 'Set it off there' : 'Set it off'}</button>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className={btn} onClick={onClose}>Close</button>
        <button type="button" className={`${btn} border-amber-600`} disabled={!ready || sending || aim.held} data-testid="blast-throw" onClick={send}>Throw</button>
      </div>
    </div>
  );
}

/** The dice bag's "Throw grenade…" and its form: nothing where the system has no blast rule. */
export function BlastLauncher({ isDm, priv, className }: { isDm: boolean; priv: boolean; className?: string }) {
  const offered = useBlastsOffered();
  const [open, setOpen] = useState(false);
  if (!offered) return null;
  return open
    ? <BlastForm isDm={isDm} priv={priv} onClose={() => setOpen(false)} />
    : <button type="button" className={className ?? `${btn} self-start`} data-testid="blast-open" onClick={() => setOpen(true)} title="A grenade thrown at a spot on the board: the table rolls the throw and the scatter, draws the blast, and each creature caught resists.">Throw grenade…</button>;
}
